import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { ArrowRightIcon, EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import { createFileRoute, Link, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useToggle } from "usehooks-ts";
import z from "zod";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useHydrated } from "@/hooks/use-hydrated";
import { authClient } from "@/integrations/auth/client";
import { signInWithSupabasePasswordServerFn } from "@/integrations/auth/supabase-functions";
import { isSupabaseAuthProvider } from "@/utils/auth-provider";
import { getUserFacingError } from "@/utils/user-facing-error";

import { SocialAuth } from "./-components/social-auth";

export const Route = createFileRoute("/auth/login")({
  component: RouteComponent,
  validateSearch: z.object({
    error: z.string().optional(),
  }),
  beforeLoad: async ({ context }) => {
    if (context.session) throw redirect({ to: "/dashboard", replace: true });
    return { session: null };
  },
});

const formSchema = z.object({
  identifier: z.string().trim().toLowerCase(),
  password: z.string().trim().min(6).max(64),
});

type FormValues = z.infer<typeof formSchema>;

function RouteComponent() {
  const router = useRouter();
  const navigate = useNavigate();
  const [showPassword, toggleShowPassword] = useToggle(false);
  const hydrated = useHydrated();
  const { flags } = Route.useRouteContext();
  const { error } = Route.useSearch();
  const isSupabaseAuth = isSupabaseAuthProvider(flags.authProvider);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      identifier: "",
      password: "",
    },
  });

  useEffect(() => {
    if (hydrated) form.setFocus("identifier");
  }, [form, hydrated]);

  const onSubmit = async (data: FormValues) => {
    const toastId = toast.loading(t`Signing in...`);

    try {
      if (isSupabaseAuth) {
        if (!data.identifier.includes("@")) {
          toast.error(t`Please sign in with your invited email address.`, { id: toastId });
          return;
        }

        const result = await signInWithSupabasePasswordServerFn({
          data: { email: data.identifier, password: data.password },
        });

        if (!result.ok) {
          toast.error(getUserFacingError({ message: result.error }, t`Failed to sign in. Please try again.`), {
            id: toastId,
          });
          return;
        }

        toast.dismiss(toastId);
        await router.invalidate();
        void navigate({ to: "/dashboard/resumes", replace: true });
        return;
      }

      const isEmail = data.identifier.includes("@");

      const result = isEmail
        ? await authClient.signIn.email({ email: data.identifier, password: data.password })
        : await authClient.signIn.username({ username: data.identifier, password: data.password });

      if (result.error) {
        toast.error(getUserFacingError(result.error, t`Failed to sign in. Please try again.`), { id: toastId });
        return;
      }

      const requiresTwoFactor =
        result.data &&
        typeof result.data === "object" &&
        "twoFactorRedirect" in result.data &&
        result.data.twoFactorRedirect;

      // Credential check passed, but the account still requires a 2FA verification step.
      if (requiresTwoFactor) {
        toast.dismiss(toastId);
        void navigate({ to: "/auth/verify-2fa", replace: true });
        return;
      }

      // Refresh route context so protected routes can read the newly established session.
      toast.dismiss(toastId);
      await router.invalidate();
      void navigate({ to: "/dashboard/resumes", replace: true });
    } catch {
      toast.error(t`Failed to sign in. Please try again.`, { id: toastId });
    }
  };

  return (
    <>
      <div className="space-y-1 text-center">
        <h1 className="text-2xl font-bold tracking-tight">
          {isSupabaseAuth ? <Trans>Sign in to MultiResume</Trans> : <Trans>Sign in to your account</Trans>}
        </h1>

        {isSupabaseAuth && (
          <p className="text-muted-foreground">
            <Trans>Use the email address that received your MultiResume invitation.</Trans>
          </p>
        )}

        {!flags.disableSignups && (
          <div className="text-muted-foreground">
            <Trans>
              Have an invitation?{" "}
              <Button
                variant="link"
                nativeButton={false}
                className="h-auto gap-1.5 px-1! py-0"
                render={
                  <Link to="/auth/register" data-phase6-entry="login-to-registration">
                    <Trans>Use invitation to register</Trans> <ArrowRightIcon />
                  </Link>
                }
              />
            </Trans>
          </div>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>
            <Trans>Sign-in link issue</Trans>
          </AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!flags.disableEmailAuth && (
        <Form {...form}>
          <form method="post" onSubmit={form.handleSubmit(onSubmit)}>
            <fieldset disabled={!hydrated} className="m-0 min-w-0 space-y-6 border-0 p-0">
              <FormField
                control={form.control}
                name="identifier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Trans>Email Address</Trans>
                    </FormLabel>
                    <FormControl
                      render={
                        <Input
                          autoFocus
                          autoComplete="section-login username webauthn"
                          placeholder={t`name@example.com`}
                          className="lowercase"
                          {...field}
                        />
                      }
                    />
                    <FormMessage />
                    {!isSupabaseAuth && (
                      <FormDescription>
                        <Trans>You can also use your username to login.</Trans>
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>
                        <Trans>Password</Trans>
                      </FormLabel>

                      <Button
                        tabIndex={-1}
                        variant="link"
                        nativeButton={false}
                        className="h-auto p-0 text-xs leading-none"
                        render={
                          <Link to="/auth/forgot-password">
                            <Trans>Forgot Password?</Trans>
                          </Link>
                        }
                      />
                    </div>
                    <div className="flex items-center gap-x-1.5">
                      <FormControl
                        render={
                          <Input
                            min={6}
                            max={64}
                            type={showPassword ? "text" : "password"}
                            autoComplete="section-login current-password"
                            {...field}
                          />
                        }
                      />

                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={showPassword ? t`Hide password` : t`Show password`}
                        onClick={toggleShowPassword}
                      >
                        {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={!hydrated || form.formState.isSubmitting}>
                <Trans>Sign in</Trans>
              </Button>
            </fieldset>
          </form>
        </Form>
      )}

      {!isSupabaseAuth && <SocialAuth />}
    </>
  );
}
